import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/store/authStore';
import { createManifest, getVisibleProjects } from '@/services/db/pob';

export default function POBNewManifestPage() {
  const navigate = useNavigate();
  const projectIdClaim = useAuthStore((s) => s.projectId);

  const projectsQ = useQuery({
    queryKey: ['pob_projects'],
    queryFn: getVisibleProjects,
  });

  const [projectId, setProjectId] = useState<string>(projectIdClaim ?? '');
  const [manifestDate, setManifestDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [facilityName, setFacilityName] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!projectId) {
      setError('Select a project');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const manifest = await createManifest(
        projectId,
        manifestDate,
        facilityName || null
      );
      navigate(`/pob/${manifest.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create manifest');
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-6 text-2xl font-semibold tracking-tight">
        New POB Manifest
      </h1>

      <Card>
        <CardHeader>
          <p className="text-sm text-muted-foreground">
            Create a Personnel on Board manifest. One manifest per project per
            date — if one already exists, you'll be taken to it.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!projectIdClaim && (
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select value={projectId} onValueChange={(v) => setProjectId(v ?? '')}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project">
                    {(projectsQ.data ?? []).find((p) => p.id === projectId)
                      ?.name ??
                      (projectId ? 'Loading project…' : 'Select project')}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {(projectsQ.data ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label>Manifest Date *</Label>
            <Input
              type="date"
              value={manifestDate}
              onChange={(e) => setManifestDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Facility Name</Label>
            <Input
              type="text"
              placeholder="e.g. Rig Boomvang"
              value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => navigate('/pob')}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="bg-mozaic-teal hover:bg-mozaic-teal/90"
            >
              {submitting ? 'Creating...' : 'Create Manifest'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
